import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ModalFooter, ModalFormSection } from 'components/common/modals';
import { ButtonV2 } from 'components/common/ui';
import { FieldV2 } from 'components/common/form-controls';

import {
  languagesOptions,
  regionOptions,
} from 'components/bookings/NewBookingForm/constants';
import b2BAddOrderValidationSchema from './b2BOrderValidationSchema';
import styles from './b2BOrderForm.style';
import { B2BOrderBookingFields } from '../B2BOrderBookingFields/B2BOrderBookingFields';

const B2BOrderForm = ({
  defaultValues = {},
  vehicles,
  isLoading,
  onSubmit,
}) => {
  const resolver = yupResolver(b2BAddOrderValidationSchema);

  const methods = useForm({
    defaultValues,
    resolver,
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'insertProducts', // unique name for your Field Array
  });

  const handleAddBooking = (
    value = {
      vehicle: '',
      contractPeriod: '',
      quantity: '',
      price: '',
      discount: '',
    }
  ) => {
    append(value);
  };

  useEffect(() => {
    handleAddBooking();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleReset = () => {
    methods.reset();
    handleAddBooking();
  };

  const handleRemoveBooking = (index) => remove(index);

  const shouldDisableFields = !!defaultValues?.id;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalFormSection title="User Details">
          <Box sx={styles.row}>
            <FieldV2
              name="firstName"
              disabled={shouldDisableFields || isLoading}
              isRequired
              control={control}
              label="First Name"
            />
            <FieldV2
              name="lastName"
              disabled={shouldDisableFields || isLoading}
              isRequired
              control={control}
              label="Last Name"
            />
            <FieldV2
              name="email"
              disabled={shouldDisableFields || isLoading}
              isRequired
              control={control}
              label="Email Address"
            />
            <FieldV2
              name="phoneNumber"
              disabled={shouldDisableFields || isLoading}
              isRequired
              control={control}
              label="Phone Number"
              helperText="Phone Number should follow the E.164 format, e.g. +30xxxxxxxxxx"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              name="language"
              disabled={shouldDisableFields || isLoading}
              isRequired
              control={control}
              type="dropdown"
              options={languagesOptions}
              label="Language"
            />
            <FieldV2
              name="region"
              disabled={shouldDisableFields || isLoading}
              isRequired
              type="dropdown"
              options={regionOptions}
              control={control}
              label="Region"
            />
            <FieldV2
              name="orderName"
              isRequired
              disabled={shouldDisableFields || isLoading}
              control={control}
              label="Order Name"
            />
          </Box>
        </ModalFormSection>
        <ModalFormSection title="Consent">
          <Box sx={styles.row}>
            <FieldV2
              disabled
              label="Consent TOS"
              name="consentTOS"
              type="checkbox"
              control={methods.control}
              wrapperStyle={styles.checkBoxWrapper}
            />

            <FieldV2
              label="Consent Newsletter"
              name="consentNewsletter"
              type="checkbox"
              control={methods.control}
              wrapperStyle={styles.checkBoxWrapper}
            />
            <FieldV2
              label="Consent contract"
              name="consentContract"
              type="checkbox"
              control={methods.control}
              wrapperStyle={styles.checkBoxWrapper}
            />
            <FieldV2
              label="Send mail"
              name="sendEmail"
              type="checkbox"
              control={methods.control}
              wrapperStyle={styles.checkBoxWrapper}
            />
          </Box>
        </ModalFormSection>
        <ModalFormSection title="Vehicles Details">
          <B2BOrderBookingFields
            methods={methods}
            fields={fields}
            isLoading={isLoading}
            vehicles={vehicles}
            onAdd={handleAddBooking}
            onRemove={fields?.length > 1 && handleRemoveBooking}
          />
        </ModalFormSection>
        <ModalFooter>
          <ButtonV2
            type="button"
            disabled={isSubmitting || isLoading}
            variant="outlined"
            onClick={handleReset}
          >
            Reset all
          </ButtonV2>
          <ButtonV2 type="submit" disabled={isSubmitting || isLoading}>
            Save
          </ButtonV2>
        </ModalFooter>
      </form>
    </FormProvider>
  );
};

export default B2BOrderForm;
